import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, MessageSquare, Calendar as CalendarIcon, AlertCircle, Star, ChevronRight, Phone, Mail, MapPin, Clock, Globe, Building2, HelpCircle, CheckSquare, ExternalLink } from 'lucide-react';
import Navigation from '../components/Navigation';
import OnboardingWizard from '../components/OnboardingWizard';
import { toast } from 'sonner';
import { trackFeatureUsage } from '../utils/analytics';
import { formatPhoneNumber, getPhoneLink } from '../utils/phoneFormat';
import { formatDate } from '../utils/dateFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Helper function to render text with clickable links - inlined to ensure deployment
const renderTextWithLinks = (text) => {
  if (!text) return null;
  
  // URL regex pattern - matches http and https URLs
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  const parts = text.split(urlPattern);
  
  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part && part.match(/^https?:\/\//)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#80298F] hover:underline inline-flex items-center gap-1 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
          <ExternalLink className="h-3 w-3 inline-block flex-shrink-0" />
        </a>
      );
    }
    return part ? <span key={index}>{part}</span> : null;
  });
};

function ClientDashboard({ user }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [firmInfo, setFirmInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    fetchData();
    // Track dashboard visit for client analytics
    trackFeatureUsage('Dashboard');
  }, []);

  // Separate effect for onboarding check - runs after user is confirmed
  useEffect(() => {
    if (user?.user_id && user?.role === 'client') {
      const hasSeenOnboarding = localStorage.getItem(`onboarding_completed_${user.user_id}`);
      const forceShowTour = localStorage.getItem(`show_tour_${user.user_id}`);
      
      // If user clicked "Take Portal Tour", show onboarding and clear the flag
      if (forceShowTour === 'true') {
        localStorage.removeItem(`show_tour_${user.user_id}`);
        setShowOnboarding(true);
        return;
      }
      
      // Only show onboarding if specifically NOT seen (not just missing key for legacy users)
      if (hasSeenOnboarding === null) {
        // Check if this is likely a new user (created within last hour)
        const createdAt = new Date(user.created_at);
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        if (createdAt > oneHourAgo) {
          setShowOnboarding(true);
        } else {
          // Legacy user - mark as completed silently
          localStorage.setItem(`onboarding_completed_${user.user_id}`, 'true');
        }
      }
    }
  }, [user?.user_id, user?.role, user?.created_at]);

  const handleOnboardingComplete = () => {
    localStorage.setItem(`onboarding_completed_${user?.user_id}`, 'true');
    setShowOnboarding(false);
    toast.success('Welcome to your client portal!');
  };

  // Function to replay onboarding tour
  const replayOnboarding = () => {
    setShowOnboarding(true);
  };

  const fetchData = async () => {
    try {
      const [casesRes, tasksRes, announcementsRes, firmRes] = await Promise.all([
        fetch(`${API}/cases`, { credentials: 'include' }),
        fetch(`${API}/tasks`, { credentials: 'include' }),
        fetch(`${API}/announcements`, { credentials: 'include' }),
        fetch(`${API}/firm-info`, { credentials: 'include' })
      ]);

      if (casesRes.ok) setCases(await casesRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (announcementsRes.ok) setAnnouncements(await announcementsRes.json());
      if (firmRes.ok) setFirmInfo(await firmRes.json());
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  // Format phone number for tel: link
  const getPhoneLink = (phone) => {
    if (!phone) return '';
    return 'tel:+1' + phone.replace(/\D/g, '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      {/* Onboarding Wizard for first-time clients */}
      {showOnboarding && (
        <OnboardingWizard 
          onComplete={handleOnboardingComplete} 
          userName={user?.name}
        />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Announcements */}
        {announcements.length > 0 && (
          <div className="mb-6" data-testid="announcements-section">
            {announcements.map((ann) => (
              <div key={ann.announcement_id} className="bg-[#FAA519]/10 border-l-4 border-[#FAA519] p-4 rounded-r-lg">
                <h3 className="font-semibold text-gray-900">{ann.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{ann.content}</p>
              </div>
            ))}
          </div>
        )}

        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="welcome-heading">
            Welcome back, {user.name.split(' ')[0]}
          </h1>
          <p className="text-gray-600 mt-1">Here&apos;s an overview of your cases and tasks</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow" data-testid="active-cases-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Cases</p>
                <p className="text-3xl font-bold text-[#80298F] mt-2">{cases.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#80298F]/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-[#80298F]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow" data-testid="pending-tasks-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Tasks</p>
                <p className="text-3xl font-bold text-[#FAA519] mt-2">
                  {tasks.filter(t => !t.completed).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#FAA519]/10 flex items-center justify-center">
                <CalendarIcon className="h-6 w-6 text-[#FAA519]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread Messages</p>
                <p className="text-3xl font-bold text-[#DC3239] mt-2">0</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#DC3239]/10 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-[#DC3239]" />
              </div>
            </div>
          </div>
        </div>

        {/* Cases */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8" data-testid="cases-section">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-heading font-bold text-gray-900">My Cases</h2>
          </div>

          {cases.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No cases assigned yet</p>
          ) : (
            <div className="space-y-4">
              {cases.map((caseItem) => (
                <Link
                  key={caseItem.case_id}
                  to={`/cases/${caseItem.case_id}`}
                  className="block border border-gray-200 rounded-lg p-4 hover:border-[#80298F] hover:shadow-md transition-all"
                  data-testid={`case-${caseItem.case_id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-semibold text-gray-900">{caseItem.case_name}</h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-mono">
                          {caseItem.case_number}
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="text-xs font-semibold text-[#80298F] uppercase tracking-wide">Case Status:</p>
                          <span className="status-badge active">
                            {caseItem.current_status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{caseItem.status_description.split('\n\n')[0]}</p>
                      </div>
                      <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                        <span>Type: {caseItem.case_type.replace('_', ' ')}</span>
                        <span>•</span>
                        <span>Updated: {formatDate(caseItem.status_updated_at)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-8" data-testid="tasks-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-bold text-gray-900">My Tasks</h2>
            <button
              onClick={() => navigate('/my-tasks')}
              className="flex items-center gap-1 text-sm text-[#80298F] hover:text-[#9b3ba8] font-medium transition-colors"
            >
              View All Tasks
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {tasks.filter(t => !t.completed).length > 0 ? (
            <div className="space-y-4">
              {tasks.filter(t => !t.completed).slice(0, 5).map((task) => {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                const isOverdue = new Date(task.due_date) < now;
                
                return (
                  <div 
                    key={task.task_id} 
                    className={`p-4 border rounded-lg ${
                      isOverdue ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-gray-900">{task.title}</h4>
                          {isOverdue && (
                            <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                              <AlertCircle className="h-3 w-3" />
                              Overdue
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                          Due: {formatDate(task.due_date)}
                        </p>
                        {task.description && (
                          <div className="mt-2 text-sm text-gray-600">
                            {task.description.includes('Why this is needed:') ? (
                              <>
                                <p>{renderTextWithLinks(task.description.split('Why this is needed:')[0].trim())}</p>
                                <p className="mt-2 text-gray-500">
                                  <span className="font-medium text-gray-700">Why this is needed:</span>{' '}
                                  <span className="italic">{renderTextWithLinks(task.description.split('Why this is needed:')[1]?.trim())}</span>
                                </p>
                              </>
                            ) : (
                              <p>{renderTextWithLinks(task.description)}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
                        isOverdue ? 'bg-red-100 text-red-800' :
                        task.priority === 'high' || task.priority === 'urgent' ? 'bg-orange-100 text-orange-800' : 
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {isOverdue ? 'Overdue' : task.priority}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No tasks assigned yet</p>
            </div>
          )}
        </div>

        {/* Share Feedback */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/feedback')}
            className="w-full bg-gradient-to-r from-[#80298F] to-[#9b3ba8] rounded-xl shadow-md p-6 hover:shadow-lg transition-all text-left group"
            data-testid="feedback-link"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 rounded-full p-3">
                <Star className="h-6 w-6 text-[#FAA519]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1 group-hover:underline">Share Your Feedback</h3>
                <p className="text-sm text-white/80">Help us improve our service - your opinion matters!</p>
              </div>
            </div>
          </button>
        </div>

        {/* Firm Contact Information */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-testid="firm-contact-section">
          <div className="bg-gradient-to-r from-[#80298F] to-[#9b3ba8] px-6 py-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-white" />
              <h2 className="text-xl font-heading font-bold text-white">Contact Information</h2>
            </div>
          </div>
          
          {firmInfo && (
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Contact Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">{firmInfo.firm_name}</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-[#80298F] mt-0.5 shrink-0" />
                    <div>
                      <a href={getPhoneLink(firmInfo.phone)} className="text-gray-900 font-medium hover:text-[#80298F] hover:underline">
                        {formatPhoneNumber(firmInfo.phone)}
                      </a>
                      {firmInfo.fax && <p className="text-sm text-gray-500">Fax: {formatPhoneNumber(firmInfo.fax)}</p>}
                    </div>
                  </div>
                  
                  {firmInfo.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-[#80298F] mt-0.5 shrink-0" />
                    <a 
                      href={firmInfo.website.startsWith('http') ? firmInfo.website : `https://${firmInfo.website}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#80298F] hover:underline"
                    >
                      {firmInfo.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                  )}
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#80298F] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-gray-900">{firmInfo.address_line1}</p>
                      {firmInfo.address_line2 && <p className="text-gray-900">{firmInfo.address_line2}</p>}
                      <p className="text-gray-900">{firmInfo.city}, {firmInfo.state} {firmInfo.zip_code}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#80298F] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-gray-900">{firmInfo.hours_english}</p>
                      {firmInfo.hours_note_english && <p className="text-sm text-gray-500 italic">{firmInfo.hours_note_english}</p>}
                      {firmInfo.hours_spanish && <p className="text-sm text-gray-500 mt-1">{firmInfo.hours_spanish}</p>}
                      {firmInfo.hours_note_spanish && <p className="text-sm text-gray-500 italic">{firmInfo.hours_note_spanish}</p>}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Staff Directory */}
              <div>
                <h3 className="font-semibold text-gray-900 text-lg mb-4">Staff Directory</h3>
                {firmInfo.staff_directory && firmInfo.staff_directory.length > 0 ? (
                <div className="space-y-3">
                  {firmInfo.staff_directory.map((staff, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{staff.name}</p>
                        <a href={`mailto:${staff.email}`} className="text-sm text-[#80298F] hover:underline">
                          {staff.email}
                        </a>
                      </div>
                      {staff.extension && (
                        <span className="text-sm text-gray-500 bg-white px-2 py-1 rounded border">Ext. {staff.extension}</span>
                      )}
                    </div>
                  ))}
                </div>
                ) : (
                  <p className="text-gray-500 text-sm">No staff directory available</p>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </main>

      {/* Help Button - Fixed position */}
      <button
        onClick={() => navigate('/help')}
        className="fixed bottom-6 right-6 bg-[#80298F] text-white p-4 rounded-full shadow-lg hover:bg-[#6B2278] transition-all hover:scale-105 z-40"
        data-testid="help-button"
        title="Need Help?"
      >
        <HelpCircle className="h-6 w-6" />
      </button>
    </div>
  );
}

export default ClientDashboard;